import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import {
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
// tslint:disable:nx-enforce-module-boundaries
import { BuilderContentModule } from '@epicuro-next/addons/builder-content';
import { ConnectModule } from '@epicuro-next/addons/connect';
import { BadgeComponent } from '@epicuro-next/ataraxia/badge';
import { DocumentVisibilityModule } from '@epicuro-next/ataraxia/document-visibility';
import { FilterSelectItemComponent } from '@epicuro-next/ataraxia/filter-select';
import { HorizontalProductCardComponent } from '@epicuro-next/ataraxia/horizontal-product-card';
import { fac, far, fas, IconsModule } from '@epicuro-next/ataraxia/icons';
import { markedOptionsFactory } from '@epicuro-next/ataraxia/markdown';
import { ModalModule } from '@epicuro-next/ataraxia/modal';
import { SPRITES_PATHS_TOKEN } from '@epicuro-next/ataraxia/models';
import { AtaraxiaShowMoreCollapseComponent } from '@epicuro-next/ataraxia/show-more-collapse';
import { YOUTUBE_API_URL_TOKEN } from '@epicuro-next/ataraxia/youtube-player';
import { FULL_PAGE_BASKET_MANAGER_NAMESPACE } from '@epicuro-next/constants/rule-engine/basket-manager-rules';
import { POWER_LIST_NAMESPACE } from '@epicuro-next/constants/rule-engine/product-list-rules';
import { SEARCH_TABLE_FEATURE_NAME } from '@epicuro-next/constants/rule-engine/product-search-table';
import { PULSE_FEATURE_NAME } from '@epicuro-next/constants/rule-engine/pulse-rules';
import { SELLER_TOOLS_FEATURE_NAME } from '@epicuro-next/constants/rule-engine/seller-tools';
import { DataProvidersConfigurationModule } from '@epicuro-next/data-providers/configuration';
import { GlobalStoreProviderModule } from '@epicuro-next/data-providers/global-store-provider';
import { HttpProviderModule } from '@epicuro-next/data-providers/http-provider';
import { OpenedListDataProviderModule } from '@epicuro-next/data-providers/list-data-provider';
import { ProductProviderModule } from '@epicuro-next/data-providers/product-provider';
import { UtilitiesProviderModule } from '@epicuro-next/data-providers/utilities';
import {
  ACCOUNTS_API_TOKEN,
  SITES_API_TOKEN,
} from '@epicuro-next/data/accounts';
import {
  BASKET_API_TOKEN,
  BASKET_V1_API_TOKEN,
} from '@epicuro-next/data/baskets';
import { BRAND_ORGS_API_TOKEN } from '@epicuro-next/data/brand-organisations';
import { BUDGET_API_TOKEN } from '@epicuro-next/data/budget';
import { REPLENISHMENT_API_TOKEN } from '@epicuro-next/data/cmi';
import { CONNECT_API_URL_TOKEN } from '@epicuro-next/data/connect';
import {
  CONTENT_API_TOKEN,
  LEGACY_CONTENT_API_TOKEN,
} from '@epicuro-next/data/content';
import { DOCUMENTS_API_TOKEN } from '@epicuro-next/data/documents';
import { DOCUMENT_CONTROL_API_TOKEN } from '@epicuro-next/data/engagement/document-control';
import { ENGAGEMENT_API_TOKEN } from '@epicuro-next/data/engagement/engagement-models';
import { INVENTORIES_API_TOKEN } from '@epicuro-next/data/inventories';
import { PRODUCT_LIST_API_LINK_TOKEN } from '@epicuro-next/data/lists';
import { MERCHANDISE_API_TOKEN } from '@epicuro-next/data/merchandise';
import {
  AGGREGATIONS_API_TOKEN,
  ORDERS_API_TOKEN,
  ORDERS_CONFIGURATION_API_TOKEN,
  ORDERS_V2_API_TOKEN,
  ORDER_BOOK_NAMESPACE,
} from '@epicuro-next/data/order-book';
import { POCKETS_API_TOKEN } from '@epicuro-next/data/pockets';
import {
  DataPricesModule,
  PRICES_PROVIDER_TOKEN,
} from '@epicuro-next/data/prices';
import {
  PRODUCTS_API_TOKEN,
  PRODUCTS_SEARCH_API_TOKEN,
  SIZE_RUNS_API_TOKEN,
} from '@epicuro-next/data/product';
import { REPORTING_API_TOKEN } from '@epicuro-next/data/reporting-api';
import { SHARED_LINK_API_TOKEN } from '@epicuro-next/data/share-link';
import { SIGNAL_R_API_URL_TOKEN } from '@epicuro-next/data/signalr';
import { USERS_API_TOKEN } from '@epicuro-next/data/users';
import { DynamicElementsArrayRendererModule } from '@epicuro-next/dynamic-elements/array-renderer';
import { DynamicAuthorComponent } from '@epicuro-next/dynamic-elements/author';
import { DynamicElementsButtonModule } from '@epicuro-next/dynamic-elements/button';
import { DynamicElementsDropdownModule } from '@epicuro-next/dynamic-elements/dropdown';
import { DynamicElementsHotListIndicatorComponent } from '@epicuro-next/dynamic-elements/hot-list-indicator';
import { DynamicElementsIconModule } from '@epicuro-next/dynamic-elements/icon';
import { DynamicElementsLinkComponent } from '@epicuro-next/dynamic-elements/link';
import { DynamicElementsListMediaModule } from '@epicuro-next/dynamic-elements/list-media';
import { DynamicElementsListProductsModule } from '@epicuro-next/dynamic-elements/list-products';
import { OrderProductThumbComponent } from '@epicuro-next/dynamic-elements/order-product-thumb';
import { DynamicElementsStatusComponent } from '@epicuro-next/dynamic-elements/order-statuses';
import { DynamicElementsPopoverModule } from '@epicuro-next/dynamic-elements/popover';
import { DynamicElementsProductCardModule } from '@epicuro-next/dynamic-elements/product-card';
import { ScrollableContainerComponent } from '@epicuro-next/dynamic-elements/scrollable-container';
import { DynamicElementsShopTheCommercialListModule } from '@epicuro-next/dynamic-elements/shop-the-commercial-list';
import { DynamicElementsSortHeaderComponent } from '@epicuro-next/dynamic-elements/sort-header';
import { DynamicElementsTabsComponent } from '@epicuro-next/dynamic-elements/tabs';
import {
  DynamicElementsCircleTextComponent,
  DynamicElementsEditableTextComponent,
  DynamicElementsEditableTextMarkdownComponent,
  DynamicElementsSimpleTextComponent,
} from '@epicuro-next/dynamic-elements/text-editable';
import { EpicuroRoutesModule } from '@epicuro-next/epicuro-routes';
import { AccountsSelectorModule } from '@epicuro-next/features/accounts-selector';
import { DashboardModule } from '@epicuro-next/features/dashboard';
import { SimpleSelectComponent } from '@epicuro-next/features/landing-panel';
import { OrderDetailsApiInterceptor } from '@epicuro-next/features/order-book-v2';
import { SidePaneComponent } from '@epicuro-next/features/panes';
import { FeaturesSpotlightModule } from '@epicuro-next/features/spotlight';
import { MainNavigationModule } from '@epicuro-next/navigation/main-navigation';
import { AuthenticationModule } from '@epicuro-next/platform/authentication';
import {
  APP_NAME_TOKEN,
  ConfigurationModule,
} from '@epicuro-next/platform/configuration';
import { connectBearerInterceptor } from '@epicuro-next/platform/connect-bearer-interceptor';
import {
  EnvironmentModule,
  EnvironmentSettings,
  ENVIRONMENT_API,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import { GraphqlModule, GRAPHQL_API } from '@epicuro-next/platform/graphql';
import {
  I18nModule,
  LANGUAGES_API_URL_TOKEN,
} from '@epicuro-next/platform/i18n';
import { IntercomModule } from '@epicuro-next/platform/intercom';
import { IonicLoaderModule } from '@epicuro-next/platform/ionic-loader';
import {
  ANALYTICS_API_ADDRESS,
  MonitoringModule,
  REPORTING_TOKEN_API_TOKEN,
} from '@epicuro-next/platform/monitoring';
import {
  RuleInterceptor,
  RULE_ENGINE_V1_API,
  RULE_ENGINE_V2_API,
} from '@epicuro-next/platform/rule-engine';
import { sentryMetaReducer } from '@epicuro-next/platform/sentry';
import { ServicenowVirtualAgentModule } from '@epicuro-next/platform/servicenow-virtual-agent';
import { StorageModule } from '@epicuro-next/platform/storage';
import { ThirdPartyScriptsModule } from '@epicuro-next/platform/third-party-scripts';
import { TrackingModule } from '@epicuro-next/platform/tracking';
import { RecommendationPanelsModule } from '@epicuro-next/recommendation-panels';
import { AccountsStateModule } from '@epicuro-next/state/accounts-state';
import { BasketStateModule } from '@epicuro-next/state/basket-state';
import { ConnectTokenStateModule } from '@epicuro-next/state/connect-token-state';
import { DabStateModule } from '@epicuro-next/state/dab-state';
import { StateHomeStateModule } from '@epicuro-next/state/home-state';
import { StateIntercomModule } from '@epicuro-next/state/intercom';
import { MerchandiseStateModule } from '@epicuro-next/state/merchandise-state';
import { ModalStateModule } from '@epicuro-next/state/modal';
import { StatePersonalMediaModule } from '@epicuro-next/state/personal-media';
import { RecommendationPanelsStateModule } from '@epicuro-next/state/recommendation-panels-state';
import { StateReplenishmentStateModule } from '@epicuro-next/state/replenishment-state';
import { RouterStoreModule } from '@epicuro-next/state/router-store';
import { SessionStateModule } from '@epicuro-next/state/session-state';
import { SignalrStateModule } from '@epicuro-next/state/signalr-state';
import { StateSitesStateModule } from '@epicuro-next/state/site-state';
import { TrackingDatacollectorsModule } from '@epicuro-next/state/tracking-datacollectors';
import { UserStateModule } from '@epicuro-next/state/user-state';
import { THEME_API, UiModule } from '@epicuro-next/ui';
import { isString } from '@epicuro-next/utilities';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
// tslint:enable:nx-enforce-module-boundaries
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import {
  NavigationActionTiming,
  RouterState,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { Angulartics2Module } from 'angulartics2';
import { MEDIA_RELATIONS_API_TOKEN } from 'data-media-relations';
import { SUGGEST_API_TOKEN } from 'data-recommendation';
import { USER_FEEDBACK_API_TOKEN } from 'data-user-feedback';
import { UserFeedbackComponent } from 'features-user-feedback';
import { BASKET_REMINDER_API_TOKEN } from 'libs/data/basket-reminder/src/lib/tokens';
import { MarkdownModule, MARKED_OPTIONS } from 'ngx-markdown';
import { SortablejsModule } from 'nxt-sortablejs';
import { PillsNavigationComponent } from 'pills-navigation';

import {
  PendingBasketsAlertEffects,
  PendingBasketsAlertService,
} from 'pending-baskets-alert';

import { environment } from '../environments/environment';

import { AppErrorHandler } from './app-error-handler';
import { AppComponent } from './app.component';
import { UNPROTECTED_RESOURCES } from './auth/unprotected-resources';
import { NAMESPACE_SPOTLIGHT } from './configurations';
import { CookieConsentService } from './cookie-consent.service';
import { MainOverlayComponent } from './main-overlay/main-overlay.component';
import { EpcMonitoringModule } from './monitoring/epc-monitoring.module';
import {
  VersionCheckerService,
  VERSION_API_URL_TOKEN,
} from './update-checker/version-checker.service';

const useIdentityOrConnectFactory = {
  useFactory: (settings: EnvironmentSettings) => {
    const useIdentityNotConnect = isString(settings.useIdentityNotConnect)
      ? JSON.parse(settings.useIdentityNotConnect)
      : settings.useIdentityNotConnect;
    return useIdentityNotConnect
      ? environment.identityApiUrl
      : environment.connectApiUrl;
  },
  deps: [ENVIRONMENT_SETTINGS],
};

const dynamicElementsImport = [
  DynamicElementsArrayRendererModule,
  DynamicAuthorComponent,
  DynamicElementsButtonModule,
  DynamicElementsDropdownModule,
  DynamicElementsEditableTextComponent,
  DynamicElementsEditableTextMarkdownComponent,
  DynamicElementsHotListIndicatorComponent,
  DynamicElementsIconModule,
  DynamicElementsLinkComponent,
  DynamicElementsListMediaModule,
  DynamicElementsListProductsModule,
  DynamicElementsPopoverModule,
  DynamicElementsProductCardModule,
  DynamicElementsShopTheCommercialListModule,
  DynamicElementsSimpleTextComponent,
  DynamicElementsCircleTextComponent,
  DynamicElementsSortHeaderComponent,
  DynamicElementsStatusComponent,
  FilterSelectItemComponent,
  ScrollableContainerComponent,
  AtaraxiaShowMoreCollapseComponent,
  OrderProductThumbComponent,
  DynamicElementsTabsComponent,
  SimpleSelectComponent,
  HorizontalProductCardComponent,
  PillsNavigationComponent,
  BadgeComponent,
  UserFeedbackComponent,
];

const stateModules = [
  AccountsStateModule,
  BasketStateModule,
  ConnectTokenStateModule,
  DabStateModule,
  MerchandiseStateModule,
  RecommendationPanelsStateModule,
  SessionStateModule,
  SignalrStateModule,
  StateHomeStateModule,
  StateReplenishmentStateModule,
  StateSitesStateModule,
  UserStateModule,
];

@NgModule({
  declarations: [AppComponent, MainOverlayComponent],
  imports: [
    AuthenticationModule.forRoot({
      unprotectedResources: Object.values(UNPROTECTED_RESOURCES),
    }),
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FeaturesSpotlightModule,
    HttpClientModule,
    UiModule,
    GraphqlModule,
    AccountsSelectorModule,
    EpicuroRoutesModule,
    EpcMonitoringModule,
    EnvironmentModule,
    StoreRouterConnectingModule.forRoot({
      routerState: RouterState.Minimal,
      navigationActionTiming: NavigationActionTiming.PostActivation,
    }),
    // Use any as we are injecting a global level reducer, which is not intended use case
    // tslint:disable-next-line:no-any
    StoreModule.forRoot([] as any, {
      runtimeChecks: {
        strictActionWithinNgZone: true,
        strictStateSerializability: true,
      },
      metaReducers: [sentryMetaReducer],
    }),
    StoreDevtoolsModule.instrument({
      name: 'Epicuro',
      maxAge: environment.production ? 250 : 0,
      logOnly: environment.production,
      connectInZone: true,
    }),
    ...stateModules,
    EffectsModule.forRoot([PendingBasketsAlertEffects]),
    RouterStoreModule,
    ConfigurationModule.forRoot([
      NAMESPACE_SPOTLIGHT,
      ORDER_BOOK_NAMESPACE,
      FULL_PAGE_BASKET_MANAGER_NAMESPACE,
      PULSE_FEATURE_NAME,
      SELLER_TOOLS_FEATURE_NAME,
      POWER_LIST_NAMESPACE,
      SEARCH_TABLE_FEATURE_NAME,
    ]),
    StorageModule,
    MainNavigationModule,
    MonitoringModule,
    I18nModule,
    ModalModule,
    Angulartics2Module.forRoot(),
    TrackingModule.forRoot(),
    ConnectModule,
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    DocumentVisibilityModule,
    HammerModule,
    SortablejsModule.forRoot({ animation: 150 }), // Need to register in root for animations
    BuilderContentModule,
    IconsModule,
    IonicLoaderModule.forRoot(),
    IntercomModule,
    StateIntercomModule,
    DashboardModule,
    StatePersonalMediaModule,
    OpenedListDataProviderModule,
    HttpProviderModule,
    ProductProviderModule,
    GlobalStoreProviderModule,
    UtilitiesProviderModule,
    DataProvidersConfigurationModule,
    // RouterModule, // needed for sentry tracking
    ...dynamicElementsImport,
    ServicenowVirtualAgentModule,
    OverlayModule,
    TrackingDatacollectorsModule,
    ThirdPartyScriptsModule,
    RecommendationPanelsModule,
    ModalStateModule,
    SidePaneComponent,
    DataPricesModule,
  ],
  providers: [
    provideHttpClient(
      withInterceptors([connectBearerInterceptor, OrderDetailsApiInterceptor]),
    ),
    NgbActiveModal,
    {
      provide: APP_NAME_TOKEN,
      useValue: environment.appName,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RuleInterceptor,
      multi: true,
    },
    {
      provide: RULE_ENGINE_V1_API,
      useValue: environment.ruleEngineV1ApiUrl,
    },
    {
      provide: RULE_ENGINE_V2_API,
      useValue: environment.ruleEngineV2ApiUrl,
    },
    VersionCheckerService,
    {
      provide: VERSION_API_URL_TOKEN,
      useValue: environment.version,
    },
    {
      provide: LANGUAGES_API_URL_TOKEN,
      useValue: environment.availableLanguagesApiUrl,
    },
    {
      provide: THEME_API,
      useValue: UNPROTECTED_RESOURCES.THEME_API,
    },
    {
      provide: ENVIRONMENT_API,
      useValue: UNPROTECTED_RESOURCES.ENVIRONMENT_API,
    },
    {
      provide: ORDERS_CONFIGURATION_API_TOKEN,
      useValue: UNPROTECTED_RESOURCES.ORDERS_CONFIGURATION_API,
    },
    {
      provide: GRAPHQL_API,
      useValue: environment.graphqlApi,
    },
    {
      provide: SITES_API_TOKEN,
      useValue: environment.sites,
    },
    {
      provide: MERCHANDISE_API_TOKEN,
      useValue: environment.merchandiseApi,
    },
    {
      provide: PRODUCT_LIST_API_LINK_TOKEN,
      useValue: environment.productListApi,
    },
    {
      provide: ENGAGEMENT_API_TOKEN,
      useValue: environment.engagementApiUrl,
    },
    {
      provide: CONTENT_API_TOKEN,
      useValue: environment.contentApi,
    },
    {
      provide: LEGACY_CONTENT_API_TOKEN,
      useValue: environment.legacyContentApi,
    },
    {
      provide: YOUTUBE_API_URL_TOKEN,
      useValue: environment.youtubeApiUrl,
    },
    {
      provide: USERS_API_TOKEN,
      useValue: environment.usersApiUrl,
    },
    {
      provide: ACCOUNTS_API_TOKEN,
      useValue: environment.accountsApiUrl,
    },
    {
      provide: DOCUMENTS_API_TOKEN,
      useValue: environment.documentsApiUrl,
    },
    {
      provide: BRAND_ORGS_API_TOKEN,
      useValue: environment.brandorganisationsApiUrl,
    },
    {
      provide: BASKET_API_TOKEN,
      useValue: environment.basketApiUrl,
    },
    {
      provide: BASKET_V1_API_TOKEN,
      useValue: environment.basketV1ApiUrl,
    },
    {
      provide: CONNECT_API_URL_TOKEN,
      ...useIdentityOrConnectFactory,
    },
    {
      provide: SIGNAL_R_API_URL_TOKEN,
      ...useIdentityOrConnectFactory,
    },
    {
      provide: INVENTORIES_API_TOKEN,
      useValue: environment.inventoriesApiUrl,
    },
    {
      provide: ORDERS_API_TOKEN,
      useValue: environment.ordersApiUrl,
    },
    {
      provide: ORDERS_V2_API_TOKEN,
      useValue: environment.ordersV2ApiUrl,
    },
    {
      provide: SPRITES_PATHS_TOKEN,
      useValue: environment.spritesPaths,
    },
    {
      provide: PRODUCTS_API_TOKEN,
      useValue: environment.productsApiUrl,
    },
    {
      provide: PRODUCTS_SEARCH_API_TOKEN,
      useValue: environment.productsSearchApiUrs,
    },
    {
      provide: SIZE_RUNS_API_TOKEN,
      useValue: environment.sizeRunsApiUrl,
    },
    {
      provide: SUGGEST_API_TOKEN,
      useValue: environment.suggestApiUrl,
    },
    {
      provide: REPLENISHMENT_API_TOKEN,
      useValue: environment.replenishmentsApiUrl,
    },
    {
      provide: REPORTING_API_TOKEN,
      useValue: environment.reportingApi.reportingApiUrl,
    },
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    {
      provide: ANALYTICS_API_ADDRESS,
      useValue: environment.analyticsApi.azure,
    },
    {
      provide: AGGREGATIONS_API_TOKEN,
      useValue: environment.aggregationsV1ApiUrl,
    },
    {
      provide: REPORTING_TOKEN_API_TOKEN,
      useValue: environment.reportingApi.token,
    },
    {
      provide: DOCUMENT_CONTROL_API_TOKEN,
      useValue: environment.documentControlApiUrl,
    },
    {
      provide: SHARED_LINK_API_TOKEN,
      useValue: environment.sharedLinkApi,
    },
    {
      provide: BUDGET_API_TOKEN,
      useValue: environment.budget,
    },
    {
      provide: BASKET_REMINDER_API_TOKEN,
      useValue: environment.basketReminder,
    },
    {
      provide: PRICES_PROVIDER_TOKEN,
      useValue: environment.pricesApiUrl,
    },
    {
      provide: POCKETS_API_TOKEN,
      useValue: environment.pockets,
    },
    {
      provide: USER_FEEDBACK_API_TOKEN,
      useValue: environment.userFeedback,
    },
    {
      provide: MEDIA_RELATIONS_API_TOKEN,
      useValue: environment.mediaRelationsApiUrl,
    },
    CookieConsentService,
    PendingBasketsAlertEffects,
    PendingBasketsAlertService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas, far, fac);
  }
}
